'use client';

import { useState, useCallback, useEffect } from 'react';
import {
    Card,
    CardContent,
    CardHeader,
    CardTitle,
    CardDescription,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
    Copy,
    Upload,
    List,
    ListPlus,
    ListOrdered,
    ListChecks,
    Link as LinkIcon,
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { ToolPageIntro } from '../../components/seo/ToolPageIntro';
import { InformationalContent } from '../../components/seo/InformationalContent';
import { textJoinerContent } from '../../data/text-joiner-content';

type JoinMethod =
    | 'custom'
    | 'comma'
    | 'newline'
    | 'tab'
    | 'space'
    | 'semicolon'
    | 'pipe'
    | 'bullet'
    | 'numbered';

export default function TextJoiner() {
    const [inputText, setInputText] = useState('');
    const [outputText, setOutputText] = useState('');
    const [joinMethod, setJoinMethod] = useState<JoinMethod>('newline');
    const [customDelimiter, setCustomDelimiter] = useState('');
    const [prefix, setPrefix] = useState('');
    const [suffix, setSuffix] = useState('');
    const [trimLines, setTrimLines] = useState(true);
    const [skipEmpty, setSkipEmpty] = useState(true);
    const [addQuotes, setAddQuotes] = useState(false);
    const [preview, setPreview] = useState(true);

    const { toast } = useToast();

    const joinText = useCallback(() => {
        if (!inputText.trim()) {
            setOutputText('');
            return;
        }

        try {
            let lines = inputText.split('\n');

            if (trimLines) {
                lines = lines.map((line) => line.trim());
            }

            if (skipEmpty) {
                lines = lines.filter((line) => line !== '');
            }

            if (addQuotes) {
                lines = lines.map((line) => `"${line}"`);
            }

            if (prefix || suffix) {
                lines = lines.map((line) => `${prefix}${line}${suffix}`);
            }

            let delimiter = '\n';

            switch (joinMethod) {
                case 'custom':
                    delimiter = customDelimiter;
                    break;
                case 'comma':
                    delimiter = ', ';
                    break;
                case 'newline':
                    delimiter = '\n';
                    break;
                case 'tab':
                    delimiter = '\t';
                    break;
                case 'space':
                    delimiter = ' ';
                    break;
                case 'semicolon':
                    delimiter = '; ';
                    break;
                case 'pipe':
                    delimiter = ' | ';
                    break;
                case 'bullet':
                    lines = lines.map((line) => `• ${line}`);
                    delimiter = '\n';
                    break;
                case 'numbered':
                    lines = lines.map(
                        (line, index) => `${index + 1}. ${line}`
                    );
                    delimiter = '\n';
                    break;
            }

            setOutputText(lines.join(delimiter));
        } catch {
            setOutputText('An error occurred while joining text');
        }
    }, [
        inputText,
        joinMethod,
        customDelimiter,
        prefix,
        suffix,
        trimLines,
        skipEmpty,
        addQuotes,
    ]);

    useEffect(() => {
        if (preview) joinText();
    }, [preview, joinText]);

    const copyToClipboard = (text: string) => {
        navigator.clipboard.writeText(text);
        toast({
            title: 'Copied!',
            description: 'Text copied to clipboard.',
        });
    };

    const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = (event) => {
            setInputText(event.target?.result as string);
        };
        reader.readAsText(file);
    };

    return (
        <div className="container mx-auto px-6 lg:px-8 py-10">

            {/* HEADER */}
            <div className="mb-10">
                <ToolPageIntro title="Text Joiner">
                Combine multiple text strings instantly with our free online tool. Join text with custom separators for creating lists, sentences, and formatted output.
            </ToolPageIntro>
            </div>

            {/* OPTIONS */}
            <Card>
                <CardHeader>
                    <CardTitle>Join Options</CardTitle>
                    <CardDescription>
                        Choose how you want to join your text
                    </CardDescription>
                </CardHeader>

                <CardContent className="space-y-6">

                    <Tabs
                        value={joinMethod}
                        onValueChange={(v) => setJoinMethod(v as JoinMethod)}
                    >
                        <TabsList className="grid grid-cols-3 md:grid-cols-5 gap-2">
                            <TabsTrigger value="newline">New Lines</TabsTrigger>
                            <TabsTrigger value="comma">Comma</TabsTrigger>
                            <TabsTrigger value="tab">Tab</TabsTrigger>
                            <TabsTrigger value="space">Space</TabsTrigger>
                            <TabsTrigger value="semicolon">Semicolon</TabsTrigger>
                            <TabsTrigger value="pipe">Pipe</TabsTrigger>
                            <TabsTrigger value="bullet">Bullet</TabsTrigger>
                            <TabsTrigger value="numbered">Numbered</TabsTrigger>
                            <TabsTrigger value="custom">Custom</TabsTrigger>
                        </TabsList>
                    </Tabs>

                    {joinMethod === 'custom' && (
                        <div>
                            <Label>Custom Delimiter</Label>
                            <Input
                                value={customDelimiter}
                                onChange={(e) =>
                                    setCustomDelimiter(e.target.value)
                                }
                                placeholder="Enter custom delimiter"
                            />
                        </div>
                    )}

                    <div className="grid md:grid-cols-2 gap-4">
                        <div>
                            <Label>Prefix</Label>
                            <Input
                                value={prefix}
                                onChange={(e) => setPrefix(e.target.value)}
                            />
                        </div>
                        <div>
                            <Label>Suffix</Label>
                            <Input
                                value={suffix}
                                onChange={(e) => setSuffix(e.target.value)}
                            />
                        </div>
                    </div>

                    <div className="flex flex-wrap gap-6">
                        <div className="flex items-center gap-2">
                            <Switch
                                checked={trimLines}
                                onCheckedChange={setTrimLines}
                            />
                            <span>Trim Lines</span>
                        </div>

                        <div className="flex items-center gap-2">
                            <Switch
                                checked={skipEmpty}
                                onCheckedChange={setSkipEmpty}
                            />
                            <span>Skip Empty</span>
                        </div>

                        <div className="flex items-center gap-2">
                            <Switch
                                checked={addQuotes}
                                onCheckedChange={setAddQuotes}
                            />
                            <span>Add Quotes</span>
                        </div>

                        <div className="flex items-center gap-2">
                            <Switch
                                checked={preview}
                                onCheckedChange={setPreview}
                            />
                            <span>Live Preview</span>
                        </div>
                    </div>
                </CardContent>
            </Card>

            {/* INPUT / OUTPUT */}
            <div className="grid lg:grid-cols-2 gap-8 mt-10">

                <div>
                    <div className="flex justify-between items-center mb-2">
                        <Label>Input Text</Label>
                        <label className="cursor-pointer">
                            <Upload className="w-4 h-4 inline mr-1" />
                            Upload
                            <input
                                type="file"
                                hidden
                                onChange={handleFileUpload}
                            />
                        </label>
                    </div>

                    <Textarea
                        value={inputText}
                        onChange={(e) => setInputText(e.target.value)}
                        className="min-h-[350px]"
                    />
                </div>

                <div>
                    <div className="flex justify-between items-center mb-2">
                        <Label>Joined Text</Label>
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => copyToClipboard(outputText)}
                        >
                            <Copy className="w-4 h-4 mr-1" /> Copy
                        </Button>
                    </div>

                    <Textarea
                        value={outputText}
                        readOnly
                        className="min-h-[350px] bg-muted/50"
                    />
                </div>
            </div>

            {/* SEO SECTION */}
            <div className="mt-16">
                <InformationalContent
                    title={textJoinerContent.title}
                    description={textJoinerContent.description}
                    content={textJoinerContent.content}
                    faqs={textJoinerContent.faqs}
                    relatedTools={textJoinerContent.relatedTools}
                />
            </div>

        </div>
    );
}
